class UserAgent {
    // --- Intern helpers ---
    static get ua() {
        if (typeof navigator === "undefined") return "";
        return navigator.userAgent || navigator.vendor || (window.opera || "");
    }

    static get platform() {
        if (typeof navigator === "undefined") return "";
        return navigator.platform || "";
    }

    static get uaData() {
        return (typeof navigator !== "undefined" && navigator.userAgentData) || null;
    }

    // --- OS / Devices ---
    static isIOS() {
        const platform = this.platform;
        const ua = this.ua;

        if (/iPad|iPhone|iPod/.test(platform)) return true;

        if (
            platform === "MacIntel" &&
            typeof navigator !== "undefined" &&
            navigator.maxTouchPoints > 1 &&
            /Macintosh/.test(ua)
        ) {
            return true;
        }

        if (/\(iPad;/.test(ua)) return true;

        return false;
    }

    static isAndroid() {
        return /Android/i.test(this.ua);
    }
    
    static isTablet() {
        const ua = this.ua;
        if (/iPad/i.test(this.platform) || /iPad/i.test(ua)) return true;

        if (this.isAndroid()) {
            if (/Tablet/i.test(ua)) return true;
            if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return true;
        }
        if (/Tablet/i.test(ua)) return true;
        return false;
    }

    static isMobile() {
        if (this.uaData && typeof this.uaData.mobile === "boolean") {
            return this.uaData.mobile;
        }

        if (this.isTablet()) return false;

        const ua = this.ua;
        const MOBILE_RE =
            /(iphone|ipod|android.*mobile|windows phone|iemobile|opera mini|mobi|mobile safari|blackberry|bb10|webos|palm|kaios|fennec)/i;

        if (/iPhone|iPod/i.test(this.platform) || /iPhone|iPod/i.test(ua)) return true;

        if (MOBILE_RE.test(ua)) return true;

        return false;
    }

    static isDesktop() {
        return !(this.isMobile() || this.isTablet());
    }

    static isMac() {
        return /Mac/.test(this.platform) && !this.isIOS();
    }

    static isWindows() {
        return /Win/.test(this.platform);
    }

    static isLinux() {
        if (/CrOS/i.test(this.ua)) return false;
        return /Linux/.test(this.platform) || /X11; Linux|Linux/i.test(this.ua);
    }

    // --- Browsers ---
    static isSamsung() {
        return /SamsungBrowser/i.test(this.ua);
    }

    static isChrome() {
        const ua = this.ua;
        const isChromium = /(Chrome|Chromium)/.test(ua) && !/Edg|OPR|SamsungBrowser/i.test(ua);
        const isCriOS = /CriOS/i.test(ua); // Chrome iOS
        return isChromium || isCriOS;
    }

    static isFirefox() {
        return /Firefox|FxiOS/i.test(this.ua);
    }

    static isSafari() {
        const ua = this.ua;
        const isSafariToken = /Safari/i.test(ua);
        const notOthers = !/Chrome|Chromium|CriOS|Edg|EdgiOS|OPR|OPiOS|SamsungBrowser|Android/i.test(ua);
        return isSafariToken && notOthers;
    }

    static isEdge() {
        return /Edg|EdgiOS|EdgA/i.test(this.ua);
    }

    static isOpera() {
        return /OPR|OperaMini|Opera/i.test(this.ua);
    }

    static isWebkit() {
        return /AppleWebKit/i.test(this.ua);
    }

    static isDuckDuckGo() {
        return /DuckDuckGo/i.test(this.ua);
    }

    static isHuawei() {
        return /HuaweiBrowser|HUAWEI/i.test(this.ua);
    }

    static isTor() {
        return /Tor Browser|Orfox|Onion/i.test(this.ua);
    }

    static isMiui() {
        return /MiuiBrowser|Mi Browser/i.test(this.ua);
    }
    
    static isBlink() {
        return (
            this.isWebkit() &&
            /(Chrome|Chromium|CriOS|Edg|EdgiOS|OPR|OPiOS|SamsungBrowser)/i.test(this.ua)
        );
    }

    static isInternetExplorer() {
        return /MSIE|Trident/.test(this.ua);
    }

    // IABs / WebViews
    static isFacebookIAB() {
        return /\bFBAN|FBAV\b/i.test(this.ua);
    }

    static isInstagramIAB() {
        return /Instagram/i.test(this.ua);
    }

    static isTwitterIAB() {
        return /Twitter/i.test(this.ua);
    }

    static isTikTokIAB() {
        return /T(T|t)WebView|Tiktok/i.test(this.ua);
    }

    static isTelegramWebView() {
        if (typeof window === "undefined") return false;
        return !!(window.TelegramWebviewProxy || window.Telegram?.WebApp);
    }

    static isAndroidWebView() {
        const ua = this.ua;
        return (
            this.isAndroid() &&
            (/\bwv\b/.test(ua) || /Version\/\d+\.\d+ Chrome\/\d+/i.test(ua))
        );
    }

    static isEmbeddedWebView() {
        const ua = this.ua;

        if (this.isPlatformCustomWebView())
            return true;

        if (this.isAndroidWebView()) return true;

        if (/Line|Messenger|Snapchat|WeChat|GSA|Pinterest|Telegram/i.test(ua)) return true;

        if (this.isAndroid() && !/(Chrome|Chromium|Firefox|FxiOS|SamsungBrowser|OPR|OPiOS|Edg|EdgiOS)/i.test(ua)) {
            return true;
        }

        return false;
    }
    
    static isPlatformCustomWebView() {
        return this.isFacebookIAB() || this.isInstagramIAB() || this.isTwitterIAB() || 
        this.isTikTokIAB() || this.isTelegramWebView();
    }

    // --- Context ---
    static isStandalone() {
        if (typeof window === "undefined") return false;
        return (
            window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
            window.matchMedia?.("(display-mode: minimal-ui)")?.matches === true ||
            window.navigator.standalone === true // iOS
        );
    }

    static isTouchDevice() {
        if (typeof window === "undefined") return false;
        return (
            "ontouchstart" in window ||
            (typeof navigator !== "undefined" &&
                (navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0))
        );
    }

    static isRunningInIframe() {
        if (typeof window === "undefined") return false;
        try {
            return window.self !== window.top;
        } catch {
            return true;
        }
    }

    static isPWAInstallable() {
        if (typeof window === "undefined") return false;
        const hasSW = typeof navigator !== "undefined" && "serviceWorker" in navigator;
        const hasManifest =
            typeof document !== "undefined" &&
            !!document.querySelector?.('link[rel="manifest"]');
        const chromiumBIP = "onbeforeinstallprompt" in window;
        return hasSW && hasManifest && (chromiumBIP || this.isIOS()) && (this.isMobile() || this.isTablet());
    }
    
    static isMobilePWAManualInstallable() {
        return UserAgent.isFirefox() || UserAgent.isOpera() || UserAgent.isDuckDuckGo() || UserAgent.isMiui() || 
            UserAgent.isHuawei() || UserAgent.isTor() || UserAgent.isSamsung();
    }

    static getDeviceBrand() {
        const ua = this.ua;

        if (this.isIOS() || this.isMac()) return "Apple";
        if (this.isSamsung()) return "Samsung";
        if (this.isHuawei()) return "Huawei";
        if (/SM-|Samsung/i.test(ua)) return "Samsung";
        if (/Pixel/i.test(ua)) return "Google";
        if (/OnePlus/i.test(ua)) return "OnePlus";
        if (/OPPO/i.test(ua)) return "OPPO";
        if (/Vivo/i.test(ua)) return "Vivo";
        if (/HUAWEI|HONOR/i.test(ua)) return "Huawei";
        if (/Xiaomi|Redmi|POCO|Mi\s/i.test(ua)) return "Xiaomi";
        if (/Moto|Motorola/i.test(ua)) return "Motorola";
        if (/Nokia/i.test(ua)) return "Nokia";
        if (/CrOS/i.test(ua)) return "Chromebook";

        return "unknown";
    }

    static getOS() {
        if (this.isAndroid()) return "Android";
        if (this.isIOS()) return "iOS";
        if (this.isMac()) return "macOS";
        if (this.isWindows()) return "Windows";
        if (this.isLinux()) return "Linux";
        if (/CrOS/i.test(this.ua)) return "ChromeOS";
        return "Unknown";
    }
    
    static getOSVersion() {
        const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
        let match;

        // Android
        match = ua.match(/Android\s+([\d._]+)/i);
        if (match) return match[1];

        // iOS
        match = ua.match(/OS\s([\d_]+)\slike Mac OS X/i);
        if (match) return match[1].replace(/_/g, ".");

        // Windows
        match = ua.match(/Windows NT\s([\d.]+)/i);
        if (match) {
            const v = match[1];
            const map = { "10.0": "10", "6.3": "8.1", "6.2": "8", "6.1": "7" };
            return map[v] || v;
        }

        // macOS
        match = ua.match(/Mac OS X\s+([\d_]+)/i);
        if (match) return match[1].replace(/_/g, ".");

        return "unknown";
    }
    
    static getBrowserName() {
        if (this.isTelegramWebView()) return "Telegram";
        if (this.isFacebookIAB()) return "Facebook";
        if (this.isInstagramIAB()) return "Instagram";
        if (this.isTikTokIAB()) return "TikTok";
        if (this.isTwitterIAB()) return "Twitter";
        if (this.isSamsung()) return "Samsung";
        if (this.isChrome()) return "Chrome";
        if (this.isFirefox()) return "Firefox";
        if (this.isSafari()) return "Safari";
        if (this.isEdge()) return "Edge";
        if (this.isOpera()) return "Opera";
        if (this.isInternetExplorer()) return "InternetExplorer";
        if (this.isMiui()) return "Miui";
        if (this.isHuawei()) return "Huawei";
        if (this.isDuckDuckGo()) return "DuckDuckGo";
        if (this.isTor()) return "Tor";
        if (this.isAndroidWebView()) return "AndroidWebView";
        if (this.isBlink()) return "Blink";
        if (this.isWebkit()) return "Webkit";
        return "Unknown";
    }
    static getDeviceType() {
        if (this.isTablet()) return "Tablet";
        if (this.isMobile()) return "Phone";
        return "Desktop";
    }

    // --- Full info ---
    static getFullInfo() {
        return {
            ua: this.ua,
            platform: this.platform,
            browser: this.getBrowserName(),
            os: this.getOS(),
            osVersion: this.getOSVersion(),
            deviceType: this.getDeviceType(),
            brand: this.getDeviceBrand(),

            isMobile: this.isMobile(),
            isTablet: this.isTablet(),
            isDesktop: this.isDesktop(),

            isIOS: this.isIOS(),
            isAndroid: this.isAndroid(),

            isSafari: this.isSafari(),
            isChrome: this.isChrome(),
            isFirefox: this.isFirefox(),
            isEdge: this.isEdge(),
            isOpera: this.isOpera(),
            isSamsung: this.isSamsung(),

            isStandalone: this.isStandalone(),
            isTouchDevice: this.isTouchDevice(),
            isIframe: this.isRunningInIframe(),
            isPWAInstallable: this.isPWAInstallable(),

            isEmbeddedWebView: this.isEmbeddedWebView(),
            isFacebookIAB: this.isFacebookIAB(),
            isInstagramIAB: this.isInstagramIAB(),
            isTwitterIAB: this.isTwitterIAB(),
            isTikTokIAB: this.isTikTokIAB(),
            uaDataMobile: this.uaData ? this.uaData.mobile : undefined,
        };
    }
}

export default UserAgent;
